import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, message, Row, Col, Card, InputNumber, Tabs, AutoComplete } from "antd";
import { secureApi } from "../../Config/api";
import { GET, POST } from "../../utils/apis";
import { ArrowLeft } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { translations } from "../../utils/constants";
import { fetchBusLed, fetchLines, fetchLinesDirections } from "../../utils/helpers";
import { setBusLedData, setTotalBusLed, setAllLines, setAllLinesDirections, setBusLedAllData } from '../../Redux/actions/busLedAction';

const Panel = ({ setIsModalVisible }) => {
    const language = useSelector((state) => state.languageReducer?.language)
    const organizationFromRedux = useSelector((state) => state.organizationReducer?.allOrganizations)
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const user = useSelector(state => state?.authReducer?.user)
    const busLedFromRedux = useSelector((state) => state?.busLedReducer?.allBusLeds)
    const directionOptions = useSelector((state) => state?.busLedReducer?.allLinesDirections)
    const lineOptions = useSelector((state) => state?.busLedReducer?.allLines)
    // Lines of the selected client's GTFS feed(s): the Line field suggests
    // these (route short name + long name) instead of previously typed codes.
    const [gtfsLines, setGtfsLines] = useState([])
    const [gtfsLinesLoading, setGtfsLinesLoading] = useState(false)
    const currentPage = useSelector(state => state.busLedReducer.currentPage)
    const pageSize = useSelector(state => state.busLedReducer.pageSize)
    const editPanel = useSelector(state => state.panelReducer.editPanel)
    const [loading, setLoading] = useState(false);
    const [controlUnits, setControlUnits] = useState([]);
    const [selectedSerial, setSelectedSerial] = useState(editPanel?.device_id || null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedLine, setSelectedLine] = useState(editPanel?.line_code);
    const [selectedDirectios, setSelectedDirectios] = useState(editPanel?.direction_code);
    const [isEditMode, setIsEditMode] = useState(false);
    const [activeTab, setActiveTab] = useState("front");
    const [organizationId, setOrganizationId] = useState(editPanel?.organization_id || null);
    const [matchedBusLog, setMatchedBusLog] = useState(null);

    // console.log(activeTab, 'activeTab')
    useEffect(() => {
        if (organizationId && selectedSerial && selectedDirectios, selectedLine) {
            let matchBusPenal = busLedFromRedux?.find((obj) =>
                obj?.organization_id === organizationId &&
                obj?.device_id === selectedSerial &&
                obj?.line_code === selectedLine &&
                obj?.direction_code === selectedDirectios
            )
            setMatchedBusLog(matchBusPenal)
        }
    }, [organizationId, selectedSerial, selectedDirectios, selectedLine])



    const handleCancel = () => {
        fetchBusLed({ setBusLedData, setTotalBusLed, dispatch, setBusLedAllData, user, currentPage, pageSize })

        setIsModalVisible(false)

    };
    useEffect(() => {
        if (matchedBusLog) {
            setIsEditMode(true);
            setOrganizationId(matchedBusLog?.organization_id);

            form.setFieldsValue({
                brand: matchedBusLog?.brand,
                line_id: matchedBusLog?.line_code,
                direction_id: matchedBusLog?.direction_code,
                front: matchedBusLog?.front_view || {},
                side: matchedBusLog?.side_view || {},
                rear: matchedBusLog?.rear_view || {},
            });
        } else {
            setIsEditMode(false);
        }
    }, [matchedBusLog]);


    useEffect(() => {
        const fetchControlUnits = async () => {
            try {
                const res = await secureApi.get(GET.GET_CONTROL_UNIT_SERIAL_NUMBERS);
                if (res?.success) {
                    setControlUnits(res.data || []);
                }
            } catch (err) {
                console.log(err);
            }
        };
        fetchControlUnits();
        fetchLinesDirections({ setAllLinesDirections, dispatch })
        fetchLines({ setAllLines, dispatch })
    }, []);

    const serialOptions = [
        { label: 'Default', value: 'default' },
        ...controlUnits?.filter((obj) => obj?.organization_id === organizationId).map((item) => ({
            label: item.serial_number,
            value: item.serial_number,
        })),
    ];

    // Label by organization name; the client code is empty for most
    // organizations and antd then showed the raw id instead.
    const organizationOptions = organizationFromRedux.map((item) => ({
        label: item.organization_name
            ? `${item.organization_name}${item.client ? ` (${item.client})` : ''}`
            : item.client || item.id,
        value: item.id,
    }));

    const brandOptions = (() => {
        if (!selectedSerial) return [];
        const brands = controlUnits
            .filter(item => item.serial_number === selectedSerial && item.brand)
            .map(item => item.brand);
        const uniqueBrands = [...new Set(brands)];

        if (uniqueBrands.length === 0) {
            return [{ label: "-", value: "-" }];
        }

        return uniqueBrands.map(b => ({
            label: b,
            value: b,
        }));
    })();

    const handleSerialChange = (serial_number) => {
        setSelectedSerial(serial_number);

        const selected = controlUnits.find(
            (i) => i.serial_number === serial_number
        );

        if (selected && selected.brand) {
            setSelectedBrand(selected.brand);
            form.setFieldsValue({
                brand: selected.brand,
            });
        } else {
            setSelectedBrand("-");
            form.setFieldsValue({
                brand: "",
            });
        }
    };

    const handleOrginzationChange = (id) => {
        setOrganizationId(id);
    };

    // Whenever the client changes, load the lines of its GTFS feed(s).
    useEffect(() => {
        let cancelled = false
        if (!organizationId) {
            setGtfsLines([])
            return undefined
        }
        const load = async () => {
            setGtfsLinesLoading(true)
            try {
                const feedsRes = await secureApi.get(GET.GET_GTFS_FEEDS, { params: { organization_id: organizationId } })
                const feeds = (feedsRes?.success ? feedsRes?.data : []) || []
                const perFeed = await Promise.all(
                    feeds.map((f) =>
                        secureApi
                            .get(GET.GET_GTFS_LINES, { params: { feed_source: f?.feed_source } })
                            .then((r) => (r?.success ? r?.data : []) || [])
                            .catch(() => [])
                    )
                )
                const seen = new Set()
                const lines = []
                perFeed.flat().forEach((r) => {
                    const code = String(r?.line || '').trim()
                    if (!code || seen.has(code)) return
                    seen.add(code)
                    lines.push({ line: code, name: r?.route_long_name || '' })
                })
                if (!cancelled) setGtfsLines(lines)
            } catch (e) {
                if (!cancelled) setGtfsLines([])
            } finally {
                if (!cancelled) setGtfsLinesLoading(false)
            }
        }
        load()
        return () => { cancelled = true }
    }, [organizationId])

    // GTFS lines when the client has a feed; otherwise the codes used before.
    const lineAutoOptions = gtfsLines.length
        ? gtfsLines.map((l) => ({
            value: l.line,
            label: (
                <span className="panel-line-option">
                    <span className="panel-line-code">{l.line}</span>
                    {l.name ? <span className="panel-line-name">{l.name}</span> : null}
                </span>
            ),
        }))
        : (lineOptions || []).map((v) => ({ value: v }))

    const handleUpdate = async () => {
        try {
            const values = form.getFieldsValue(); // Get all form values
            // Check if a matching log exists based on organization_id and device_id
            const matchedLog = busLedFromRedux.find(
                (log) => log.organization_id === organizationId && log.device_id === selectedSerial
            );

            const payload = ["Front", "Side", "Rear"]?.map((role) => {
                const data = {
                    panel_role: role,
                    ...(values[role] ? values[role] : matchedLog[`${role}_view`]),
                }

                return { ...data, panel_role: data.panel_role.toLowerCase() };
            })
            const client = organizationFromRedux.find(org => org.id === organizationId)?.client
            // console.log('payload', payload)
            // return true
            setLoading(true); // Show loading indicator while processing
            let obj = {
                panels: payload,
                id: matchedLog ? matchedLog.id : null, // Use the ID of the matched log, or null for new
                device_id: selectedSerial || "default",
                brand: values?.brand,
                line_id: values?.line_id,
                direction_id: values?.direction_id,
                organization_id: organizationId,
                user_id: user?.id,
                client: client
            }
            // console.log(obj, 'obj')
            // If a matched log exists, we are in edit mode, otherwise it's a new entry
            const response = await secureApi.post(POST.UPDATE_BUS_PANEL, obj);

            if (response?.success) {
                message.success(matchedLog ? translations?.[language]?.panelUpdatedSuccessfully : translations?.[language]?.panelAddedSuccessfully);

                // Reset the form and set the edit mode to false
                form.resetFields();
                setIsEditMode(false);
                handleCancel(); // Clear the edit panel state if updating

            } else {
                message.error(response?.message || "Failed to save the panel");
            }
        }
        catch (error) {
            console.error(error);
            message.error("Something went wrong while updating or adding");
        }
        finally {
            setLoading(false); // Hide the loading indicator after the operation
        }
    };


    const handleSubmit = async (values) => {
        setLoading(true);
        // console.log(values, 'values')
        const matchedLog = busLedFromRedux.find(
            (log) => log.organization_id === organizationId && log.device_id === selectedSerial
        )
        const client = organizationFromRedux.find(org => org.id === organizationId)?.client;
        // console.log('client', client);
        try {
            const payload = ["Front", "Side", "Rear"]
                .map((role) => {
                    const data = {
                        panel_role: role,
                        ...(values[role] ? values[role] : matchedLog[`${role}_view`]),
                    };
                    return { ...data, panel_role: data.panel_role.toLowerCase() };
                });

            if (matchedLog) {
                const response = await secureApi.post(POST.UPDATE_BUS_PANEL, {
                    panels: payload,
                    id: matchedLog?.id,
                    device_id: selectedSerial,
                    brand: values?.brand,
                    line_id: values?.line_id,
                    direction_id: values?.direction_id,
                    organization_id: organizationId,
                    user_id: user?.id,
                    client: client
                });
                if (response?.success) {
                    message.success(translations[language]?.ledPanelUpdatedSuccessfully);
                    form.resetFields();
                    handleCancel();
                } else {
                    message.error(response?.message);
                }
            } else {
                const response = await secureApi.post(POST.ADD_BUS_PANEL, {
                    panels: payload,
                    device_id: selectedSerial || "default",
                    brand: values?.brand,
                    line_id: values?.line_id,
                    direction_id: values?.direction_id,
                    organization_id: organizationId,
                    user_id: user?.id,
                    client: client
                });
                // console.log(response, 'response');
                if (response?.success) {
                    message.success(translations?.[language]?.panelUpdatedSuccessfully);
                    form.resetFields();
                    handleCancel();
                } else {
                    message.error(response?.message);
                }
            }
        } catch (err) {
            message.error("Failed to save or update panel");
        } finally {
            setLoading(false);
        }
    }

    const handleGlobalSave = async () => {
        try {
            const values = await form.validateFields()
            const roles = ["Front", "Side", "Rear"];
            const missingRoles = [];

            roles.forEach((role) => {
                const roleData = values[role];
                if (!roleData || !roleData.model || !roleData.text) {
                    missingRoles.push(role);
                }
            });

            if (missingRoles.length > 0) {
                const roleNames = {
                    Front: translations[language]?.front || 'Front',
                    Side: translations[language]?.side || 'Side',
                    Rear: translations[language]?.rear || 'Rear',
                }
                const translatedRoles = missingRoles.map((role) => roleNames[role] || role)
                const roleText = translatedRoles.length === 1
                    ? translatedRoles[0]
                    : translatedRoles.slice(0, -1).join(", ") + ` ${translations[language]?.and || 'and'} ` + translatedRoles.slice(-1);

                const messageTemplate = translations[language]?.pleaseFillOutRequiredFields || 'Please fill out {{roles}} required fields'
                message.error(messageTemplate.replace('{{roles}}', roleText));
                return;
            }

            handleSubmit(values);
        } catch (errorInfo) {
            console.log('Validation Failed:', errorInfo);
            message.error(translations[language]?.pleaseCompleteFormRequirements || 'Please complete the form requirements');
        }
    }

    const ColorInput = ({ role, form, field }) => {
        const defaultColor = field === "text_color" ? "#ffffff" : "#000000";
        const color = Form.useWatch([role, field], form) || defaultColor;

        return (
            <div className="panel-color-field">
                <Input
                    type="color"
                    className="panel-color-swatch"
                    value={color}
                    onChange={(e) =>
                        form.setFieldsValue({
                            [role]: {
                                ...form.getFieldValue(role),
                                [field]: e.target.value,
                            },
                        })
                    }
                />

                <Input
                    value={color}
                    onChange={(e) =>
                        form.setFieldsValue({
                            [role]: {
                                ...form.getFieldValue(role),
                                [field]: e.target.value,
                            },
                        })
                    }
                    style={{ height: 40 }}
                />
            </div>
        );
    };

    const renderSection = (title, role) => {
        const colProps = { xs: 12, sm: 12, md: 12, lg: 8 };
        const keyValue = role === 'Front' ? 'front_view' : role === 'Side' ? 'side_view' : role === 'Rear' ? 'rear_view' : 'front_view';
        form.setFieldsValue({
            [role]: {
                ...matchedBusLog?.[keyValue],
            }
        })
        return (
            <>
                <h2 className="led-section-title">{role}</h2>

                <Row gutter={[12, 12]}>
                    {/* Panel Model */}
                    <Col {...colProps}>
                        <Form.Item
                            label={translations[language]?.panelModel}
                            name={[role, "model"]}
                            initialValue={matchedBusLog?.[keyValue]?.model} // Set initial value from matched data
                            rules={[
                                {
                                    required: true,
                                    message: translations[language]?.panelModelPlaceholder,
                                },
                            ]}
                        >
                            <Input
                                placeholder={translations[language]?.panelModelPlaceholder}
                                style={{ height: 42 }}
                            />
                        </Form.Item>
                    </Col>

                    {/* Text */}
                    <Col {...colProps}>
                        <Form.Item
                            label={translations[language]?.text}
                            name={[role, "text"]}
                            rules={[{ required: true, message: translations[language]?.textPlaceholder }]}
                            initialValue={matchedBusLog?.[keyValue]?.text} // Set initial value from matched data
                        >
                            <Input
                                placeholder={translations[language]?.textPlaceholder}
                                style={{ height: 42 }}
                            />
                        </Form.Item>
                    </Col>

                    {/* Effect */}
                    <Col {...colProps}>
                        <Form.Item label={translations[language]?.effect} name={[role, "effect"]} initialValue={matchedBusLog?.[keyValue]?.effect}>
                            <Select
                                placeholder={translations[language]?.effect}
                                className="custom-select"
                                style={{ width: "100%" }}
                                options={[
                                    { value: "static", label: translations[language]?.effectStatic },
                                    { value: "scroll", label: translations[language]?.effectScroll },
                                    { value: "blink", label: translations[language]?.effectBlink },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    {/* Speed */}
                    <Col {...colProps}>
                        <Form.Item label={translations[language]?.speed} name={[role, "speed"]} initialValue={matchedBusLog?.[keyValue]?.speed}>
                            <InputNumber
                                placeholder={translations[language]?.speedPlaceholder}
                                style={{ width: "100%", height: 40, lineHeight: "35px" }}
                                min={0}
                            />
                        </Form.Item>
                    </Col>

                    {/* Brightness */}
                    <Col {...colProps}>
                        <Form.Item label={translations[language]?.brightness} name={[role, "brightness"]} initialValue={matchedBusLog?.[keyValue]?.brightness}>
                            <InputNumber
                                placeholder={translations[language]?.brightnessPlaceholder}
                                style={{ width: "100%", height: 40, lineHeight: "35px" }}
                                min={0}
                                max={100}
                            />
                        </Form.Item>
                    </Col>

                    {/* Font */}
                    <Col {...colProps}>
                        <Form.Item label={translations[language]?.font} name={[role, "font"]} initialValue={matchedBusLog?.[keyValue]?.font}>
                            <InputNumber
                                min={0}
                                style={{ width: "100%", height: 40, lineHeight: "35px" }}
                                placeholder={translations[language]?.font}
                            />
                        </Form.Item>
                    </Col>

                    {/* Mode */}
                    <Col {...colProps}>
                        <Form.Item label={translations[language]?.mode} name={[role, "mode"]} initialValue={matchedBusLog?.[keyValue]?.mode}>
                            <InputNumber
                                min={0}
                                style={{ width: "100%", height: 40, lineHeight: "35px" }}
                                placeholder={translations[language]?.mode}
                            />
                        </Form.Item>
                    </Col>

                    {/* Margin */}
                    <Col {...colProps}>
                        <Form.Item label={translations[language]?.margin} name={[role, "margin"]} initialValue={matchedBusLog?.[keyValue]?.margin}>
                            <InputNumber
                                min={0}
                                style={{ width: "100%", height: 40, lineHeight: "35px" }}
                                placeholder={translations[language]?.margin}
                            />
                        </Form.Item>
                    </Col>

                    {/* Back Color */}
                    <Col {...colProps}>
                        <Form.Item label={translations[language]?.backColor || "Back Color"} initialValue={matchedBusLog?.[keyValue]?.back_color}>
                            <Form.Item name={[role, "back_color"]} noStyle>
                                <ColorInput
                                    role={role}
                                    form={form}
                                    field="back_color"
                                    defaultValue={matchedBusLog?.[keyValue]?.back_color} // Set initial value
                                />
                            </Form.Item>
                        </Form.Item>
                    </Col>

                    {/* Text Color */}
                    <Col xs={12} sm={12} md={12} lg={24}>
                        <Form.Item label={translations[language]?.textColor || "Text Color"} initialValue={matchedBusLog?.[keyValue]?.text_color}>
                            <Form.Item name={[role, "text_color"]} noStyle>
                                <ColorInput
                                    role={role}
                                    form={form}
                                    field="text_color"
                                    defaultValue={matchedBusLog?.[keyValue]?.text_color} // Set initial value
                                />
                            </Form.Item>
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    };



    return (
        <div className="main-inner-container panel-page">
            <div className="table-header-for-heading margin-bottom-20">
                <h1 className="main-heading margin-bottom-0">
                    {matchedBusLog ? translations[language]?.updatePenal : translations[language]?.addPenal}
                </h1>
                <Button
                    onClick={() => setIsModalVisible(false)}
                    className="back-button"
                    size="large"
                >
                    <ArrowLeft size={18} />
                    {translations[language]?.goBack}
                </Button>
            </div>
            <Form form={form} layout="vertical">
              <div className="vd-card panel-block">
                <div className="vd-card-header">
                    <h2 className="vd-card-title">{translations[language]?.details}</h2>
                </div>
                <div className="vd-card-body">
                <Row gutter={[16, 4]}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                            label={translations[language]?.selectClient}
                            name="orginization_id"
                            rules={[{ required: true, message: translations[language]?.pleaseSelectClient }]}
                            initialValue={organizationId}
                        >
                            <Select
                                placeholder={translations[language]?.selectClient}
                                options={organizationOptions}
                                onChange={handleOrginzationChange}
                                showSearch
                                optionFilterProp="label"
                                className="custom-select"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                            label={translations[language]?.selectDeviceHere}
                            name="device_id"
                            initialValue={selectedSerial || "default"}
                        >
                            <Select
                                disabled={!organizationId}
                                placeholder={translations[language]?.selectDeviceHere}
                                options={serialOptions}
                                onChange={handleSerialChange}
                                showSearch
                                optionFilterProp="label"
                                className="custom-select"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Row gutter={[16, 4]}>
                    {/* LINE */}
                    <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                            label={translations[language]?.line}
                            name="line_id"
                            rules={[{ required: true, message: translations[language]?.pleaseAddLine }]}
                            initialValue={matchedBusLog?.line_id}
                        >
                            <AutoComplete
                                disabled={!organizationId}
                                placeholder={translations[language]?.pleaseAddLine}
                                className="custom-select"
                                style={{ width: "100%" }}
                                options={lineAutoOptions}
                                notFoundContent={gtfsLinesLoading ? '...' : null}
                                filterOption={(inputValue, option) =>
                                    option.value
                                        .toUpperCase()
                                        .includes(inputValue.toUpperCase())
                                }
                                onChange={(value) => {
                                    const cleaned = value
                                        .replace(/[^a-zA-Z0-9]/g, "")
                                        .toUpperCase();

                                    setSelectedLine(cleaned)
                                    form.setFieldsValue({
                                        line_id: cleaned
                                    });
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === " ") {
                                        e.preventDefault();
                                    }
                                }}
                            />

                        </Form.Item>
                    </Col>

                    {/* DIRECTION */}
                    <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                            label={translations[language]?.direction}
                            name="direction_id"
                            rules={[{ required: true, message: translations[language]?.pleaseAddDirection }]}
                            initialValue={matchedBusLog?.direction_id}
                        >
                            <AutoComplete
                                disabled={!organizationId}
                                placeholder={translations[language]?.pleaseAddDirection}
                                className="custom-select"
                                style={{ width: "100%" }}
                                options={directionOptions?.map(v => ({ value: v }))}
                                filterOption={(inputValue, option) =>
                                    option.value
                                        .toUpperCase()
                                        .includes(inputValue.toUpperCase())
                                }
                                onChange={(value) => {
                                    const cleaned = value
                                        .replace(/[^a-zA-Z0-9]/g, "")
                                        .toUpperCase();

                                    setSelectedDirectios(cleaned)
                                    form.setFieldsValue({
                                        direction_id: cleaned
                                    });
                                }}
                                onKeyDown={(e) => {
                                    // Block space explicitly (UX polish)
                                    if (e.key === " ") {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 4]}>
                    <Col xs={24} sm={24} md={24} lg={24}>
                        <Form.Item
                            label={translations[language]?.brand}
                            name="brand"
                            initialValue={matchedBusLog?.brand}
                        >
                            <Input
                                placeholder={translations[language]?.brand}
                                disabled={!organizationId}
                                className="custom-inputs"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                </div>
              </div>

              <div className="vd-card panel-block">
                <div className="vd-card-body panel-tabs-body">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    className="panel-tabs"
                    items={[
                        {
                            key: "front",
                            label: translations[language]?.panelFront,
                            children: <Card className="panel-card">{renderSection("", translations[language]?.panelFront)}</Card>,
                        },
                        {
                            key: "side",
                            label: translations[language]?.panelSide,
                            children: <Card className="panel-card">{renderSection("", translations[language]?.panelSide)}</Card>,
                        },
                        {
                            key: "rear",
                            label: translations[language]?.panelRear,
                            children: <Card className="panel-card">{renderSection("", translations[language]?.panelRear)}</Card>,
                        },
                    ]}
                />
                </div>
              </div>

                <div className="led-button-row">
                    <Button className='led-cancel' onClick={() => handleCancel()}>
                        {translations[language]?.cancelButton}
                    </Button>

                    {isEditMode ? (
                        <Button type='primary' size='large' loading={loading} className='back-button' onClick={handleUpdate}>
                            {translations[language]?.updatePenal}
                        </Button>
                    ) : (
                        <Button key='action' size='large' onClick={handleGlobalSave} type='primary' loading={loading} className='back-button'>
                            {matchedBusLog ? translations[language]?.updatePenal : translations[language]?.addPenal}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    );
};

export default Panel;
